import React, { useEffect, useState } from "react";
import { Edit, Save, Plus, RotateCw, X } from "lucide-react";
import OperatorCardActions from "../components/OperatorCardActions";
import ToolbarSearch from "../components/ToolbarSearch";
import { fetchLocations, updateLocation, addLocation } from "../services/locations";

const LocationsOperators = () => {
    const [locations, setLocations] = useState([]);
    const [filteredLocations, setFilteredLocations] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [sortBy, setSortBy] = useState("location_name");
    const [sortDir, setSortDir] = useState("asc");
    const [editingId, setEditingId] = useState(null);
    const [edited, setEdited] = useState({});
    const [newLocation, setNewLocation] = useState({
        location_name: "",
        city: "",
        street: "",
        house_number: "",
        descriptions: "",
        price: "",
        status: true,
    });
    const [showModal, setShowModal] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [expandedLocationId, setExpandedLocationId] = useState(null);

    useEffect(() => {
        loadLocations();
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        const q = searchQuery.toLowerCase();
        const filtered = locations.filter((loc) =>
            (loc.location_name || "").toString().toLowerCase().includes(q) ||
            (loc.city || "").toString().toLowerCase().includes(q) ||
            (loc.street || "").toString().toLowerCase().includes(q) ||
            (loc.house_number || "").toString().toLowerCase().includes(q) ||
            (loc.descriptions || "").toString().toLowerCase().includes(q)
        );
        setFilteredLocations(filtered);
    }, [locations, searchQuery]);

    const loadLocations = async () => {
        const data = await fetchLocations();
        setLocations(data);
    };

    const handleEdit = (loc) => {
        setEditingId(loc.id);
        setEdited({ ...loc });
        setExpandedLocationId(loc.id);
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setEdited({});
    };

    const toggleLocationExpanded = (locId, isEditing) => {
        if (isEditing) return;
        setExpandedLocationId((prev) => (prev === locId ? null : locId));
    };

    const handleSave = async (id) => {
        if (edited.status === false) {
            const confirmDeactivate = window.confirm(
                "Ви впевнені, що хочете деактивувати цю локацію? Усі прив'язані девайси будуть відв'язані."
            );
            if (!confirmDeactivate) {
                setEditingId(null);
                return;
            }
        }
        await updateLocation(id, edited);
        await loadLocations();
        setEditingId(null);
    };

    const toggleSort = (field) => {
        if (sortBy === field) {
            setSortDir(sortDir === "asc" ? "desc" : "asc");
        } else {
            setSortBy(field);
            setSortDir("asc");
        }
    };

    const getSortedData = () => {
        if (!filteredLocations) return [];
        const sorted = [...filteredLocations];
        sorted.sort((a, b) => {
            let valA = a[sortBy] || "";
            let valB = b[sortBy] || "";
            valA = String(valA).toLowerCase();
            valB = String(valB).toLowerCase();
            if (sortDir === "asc") {
                return valA > valB ? 1 : valA < valB ? -1 : 0;
            } else {
                return valA < valB ? 1 : valA > valB ? -1 : 0;
            }
        });
        return sorted;
    };

    const handleAdd = async () => {
        if (!newLocation.location_name || !newLocation.city || !newLocation.street || !newLocation.house_number) {
            alert("Назва, місто, вулиця та номер будинку не можуть бути порожніми!");
            return;
        }
        await addLocation(newLocation);
        setNewLocation({
            location_name: "",
            city: "",
            street: "",
            house_number: "",
            descriptions: "",
            price: "",
            status: true,
        });
        setShowModal(false);
        await loadLocations();
    };

    return (
        <div>
            <div className="config-header">
                <div className="header-buttons">
                    <button className="add-btn toolbar-icon-btn" onClick={() => setShowModal(true)} title="Додати" aria-label="Додати">
                        <Plus size={16} />
                    </button>
                    <button className="add-btn refresh toolbar-icon-btn" onClick={loadLocations} title="Оновити" aria-label="Оновити">
                        <RotateCw size={16} />
                    </button>
                    <ToolbarSearch
                        value={searchQuery}
                        onChange={setSearchQuery}
                        placeholder="Пошук локації"
                    />
                </div>
            </div>

            {/* Модалка додавання */}
            {showModal && (
                <div className="modal-backdrop" onClick={() => setShowModal(false)}>
                    <div className="modal-content operator-mobile-modal operator-form-modal" onClick={(e) => e.stopPropagation()}>
                        <h3 className="mb-2 font-semibold">Додати локацію</h3>
                        <div className="operator-form-grid">
                            <input
                                type="text"
                                placeholder="Назва"
                                value={newLocation.location_name}
                                onChange={(e) => setNewLocation({ ...newLocation, location_name: e.target.value })}
                            />
                            <input
                                type="text"
                                placeholder="Місто"
                                value={newLocation.city}
                                onChange={(e) => setNewLocation({ ...newLocation, city: e.target.value })}
                            />
                            <input
                                type="text"
                                placeholder="Вулиця"
                                value={newLocation.street}
                                onChange={(e) => setNewLocation({ ...newLocation, street: e.target.value })}
                            />
                            <input
                                type="text"
                                placeholder="Номер будинку"
                                value={newLocation.house_number}
                                onChange={(e) => setNewLocation({ ...newLocation, house_number: e.target.value })}
                            />
                            <input
                                className="operator-form-span-2"
                                type="text"
                                placeholder="Опис"
                                value={newLocation.descriptions}
                                onChange={(e) => setNewLocation({ ...newLocation, descriptions: e.target.value })}
                            />
                            <input
                                type="number"
                                step="0.01"
                                placeholder="Ціна"
                                value={newLocation.price}
                                onChange={(e) => setNewLocation({ ...newLocation, price: e.target.value })}
                            />
                            <select
                                value={newLocation.status}
                                onChange={(e) =>
                                    setNewLocation({ ...newLocation, status: e.target.value === "true" })
                                }
                            >
                                <option value="true">Активний</option>
                                <option value="false">Неактивний</option>
                            </select>
                        </div>
                        <div className="modal-actions">
                            <button className="add-btn" onClick={handleAdd}>Додати</button>
                            <button onClick={() => setShowModal(false)} className="add-btn cancel-btn">
                                Скасувати
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Адаптивне відображення */}
            {isMobile ? (
                <div className="operator-accordion-list">
                    {getSortedData().map((loc) => {
                        const isEditing = editingId === loc.id;
                        const isExpanded = expandedLocationId === loc.id || isEditing;
                        const addressLabel = [loc.street, loc.house_number].filter(Boolean).join(" ");
                        const locationTitle = loc.location_name || addressLabel || "Без назви";
                        const statusLabel = loc.status ? "Активний" : "Неактивний";
                        const compactMeta = [addressLabel || null, statusLabel].filter(Boolean).join(" • ");

                        return (
                            <div key={loc.id} className="operator-accordion-item config-card">
                                <button
                                    type="button"
                                    className="operator-accordion-head"
                                    onClick={() => toggleLocationExpanded(loc.id, isEditing)}
                                    aria-expanded={isExpanded}
                                >
                                    <div className="operator-accordion-head-main">
                                        <div className="operator-accordion-title">{locationTitle}</div>
                                        <div className="operator-accordion-meta">{compactMeta}</div>
                                    </div>
                                    <div className="operator-accordion-caret">{isExpanded ? "▾" : "▸"}</div>
                                </button>

                                <OperatorCardActions
                                    editing={isEditing}
                                    onEdit={() => handleEdit(loc)}
                                    onSave={() => handleSave(loc.id)}
                                    onCancel={handleCancelEdit}
                                />

                                {isExpanded ? (
                                    <div className="operator-accordion-body">
                                        <div><strong>Назва:</strong> {isEditing ? (
                                            <input value={edited.location_name} onChange={(e) => setEdited({ ...edited, location_name: e.target.value })} />
                                        ) : loc.location_name}</div>

                                        <div><strong>Місто:</strong> {isEditing ? (
                                            <input value={edited.city} onChange={(e) => setEdited({ ...edited, city: e.target.value })} />
                                        ) : loc.city}</div>

                                        <div><strong>Вулиця:</strong> {isEditing ? (
                                            <input value={edited.street} onChange={(e) => setEdited({ ...edited, street: e.target.value })} />
                                        ) : loc.street}</div>

                                        <div><strong>Будинок:</strong> {isEditing ? (
                                            <input value={edited.house_number} onChange={(e) => setEdited({ ...edited, house_number: e.target.value })} />
                                        ) : loc.house_number}</div>

                                        <div><strong>Опис:</strong> {isEditing ? (
                                            <input value={edited.descriptions} onChange={(e) => setEdited({ ...edited, descriptions: e.target.value })} />
                                        ) : loc.descriptions}</div>

                                        <div><strong>Ціна:</strong> {isEditing ? (
                                            <input type="number" step="0.01" value={edited.price} onChange={(e) => setEdited({ ...edited, price: e.target.value })} />
                                        ) : loc.price}</div>

                                        <div><strong>Статус:</strong> {isEditing ? (
                                            <select value={edited.status} onChange={(e) => setEdited({ ...edited, status: e.target.value === "true" })}>
                                                <option value="true">Активний</option>
                                                <option value="false">Неактивний</option>
                                            </select>
                                        ) : statusLabel}</div>
                                    </div>
                                ) : null}
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="table-responsive compact">
                    <table>
                        <thead>
                            <tr>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("location_name")}>
                                    Назва {sortBy === "location_name" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("city")}>
                                    Місто {sortBy === "city" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("street")}>
                                    Вулиця {sortBy === "street" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("house_number")}>
                                    Будинок {sortBy === "house_number" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("descriptions")}>
                                    Опис {sortBy === "descriptions" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("price")}>
                                    Ціна {sortBy === "price" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("status")}>
                                    Статус {sortBy === "status" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th>Дії</th>
                            </tr>
                        </thead>
                        <tbody>
                            {getSortedData().map((loc) => (
                                <tr key={loc.id}>
                                    <td>
                                        {editingId === loc.id ? (
                                            <input value={edited.location_name} onChange={(e) => setEdited({ ...edited, location_name: e.target.value })} />
                                        ) : loc.location_name}
                                    </td>
                                    <td>
                                        {editingId === loc.id ? (
                                            <input value={edited.city} onChange={(e) => setEdited({ ...edited, city: e.target.value })} />
                                        ) : loc.city}
                                    </td>
                                    <td>
                                        {editingId === loc.id ? (
                                            <input value={edited.street} onChange={(e) => setEdited({ ...edited, street: e.target.value })} />
                                        ) : loc.street}
                                    </td>
                                    <td>
                                        {editingId === loc.id ? (
                                            <input value={edited.house_number} onChange={(e) => setEdited({ ...edited, house_number: e.target.value })} />
                                        ) : loc.house_number}
                                    </td>
                                    <td>
                                        {editingId === loc.id ? (
                                            <input value={edited.descriptions} onChange={(e) => setEdited({ ...edited, descriptions: e.target.value })} />
                                        ) : loc.descriptions}
                                    </td>
                                    <td>
                                        {editingId === loc.id ? (
                                            <input type="number" step="0.01" value={edited.price} onChange={(e) => setEdited({ ...edited, price: e.target.value })} />
                                        ) : loc.price}
                                    </td>
                                    <td>
                                        {editingId === loc.id ? (
                                            <select value={edited.status} onChange={(e) => setEdited({ ...edited, status: e.target.value === "true" })}>
                                                <option value="true">Активний</option>
                                                <option value="false">Неактивний</option>
                                            </select>
                                        ) : loc.status ? "Активний" : "Неактивний"}
                                    </td>
                                    <td className="actions">
                                        {editingId === loc.id ? (
                                            <>
                                                <button className="icon-button" onClick={() => handleSave(loc.id)} title="Зберегти">
                                                    <Save className="icon-btn" />
                                                </button>
                                                <button className="icon-button" onClick={handleCancelEdit} title="Скасувати">
                                                    <X className="icon-btn" />
                                                </button>
                                            </>
                                        ) : (
                                            <button className="icon-button" onClick={() => handleEdit(loc)} title="Редагувати">
                                                <Edit className="icon-btn" />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default LocationsOperators;
